import { Award, Globe, Users, TrendingUp, Target, Compass } from 'lucide-react'
import { COMPANY } from '../config'
import { JourneyTimeline } from '../components/JourneyTimeline'

export function AboutView({ onNavigate }) {
  return (
    <main>
      <section className="relative h-80 bg-brand-navy px-4 sm:px-6 lg:px-8">
        <img
          src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&fit=crop&w=1600&q=80"
          alt="Warehouse racks stocked with palletised goods"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-start justify-center text-white">
          <span className="mb-3 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-gold-light">
            Our Business
          </span>
          <h1 className="font-display text-4xl font-bold sm:text-5xl">About Alpha Import Export</h1>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">What we do</h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-slate">
            {COMPANY.legalName} is a trade bridge between verified producers and buyers across Europe, Africa, Asia and the Americas.
            At Alpha Import Export, we source metals, chemicals, frozen foods, beverages and agricultural produce in both
            retail-ready and bulk formats, then coordinate packaging, documentation and freight so every order arrives on time and in spec.
          </p>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 text-center">
            <span className="mb-3 inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-navy">
              Our reach
            </span>
            <h3 className="font-display text-xl font-bold text-brand-ink sm:text-2xl">
              One partner, every commodity
            </h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Metals, Ores & Industrial Materials', img: 'https://images.unsplash.com/photo-1523848309072-c199db53f137?auto=format&fit=crop&w=800&q=80' },
              { label: 'Chemicals & Industrial Ingredients', img: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=800&q=80' },
              { label: 'Frozen Food & Meat', img: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=800&q=80' },
              { label: 'Alcoholic & Non-Alcoholic Beverages', img: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=800&q=80' },
              { label: 'Agricultural Products & Produce', img: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=800&q=80' },
              { label: 'Industrial Manufacturing Supply', img: 'https://images.unsplash.com/photo-1567789884554-0b844b597180?auto=format&fit=crop&w=800&q=80' },
            ].map((t) => (
              <figure key={t.label} className="group relative h-52 overflow-hidden rounded-2xl shadow-card">
                <img
                  src={t.img}
                  alt={t.label}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 via-white/70 to-transparent px-4 pb-3 pt-10 text-sm font-semibold text-brand-ink">
                  {t.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-shell px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Globe, title: '40+ countries', text: 'Active sourcing and delivery corridors across Europe, Africa, the Middle East and Asia — each one managed by our regional trade team.' },
              { icon: Users, title: '500+ producers', text: 'A vetted network of growers, mills and manufacturers that Alpha Import Export audits before onboarding and re-scores after every shipment.' },
              { icon: Award, title: 'Certified', text: 'ISO, Halal, Kosher and Organic compliance support — certificates of analysis, origin and inspection reports verified before every departure.' },
              { icon: TrendingUp, title: 'Scalable', text: 'From single pallets to full container loads — our volumes flex with your demand without forcing you into contracts you cannot fulfil.' }
            ].map((s) => (
              <div key={s.title} className="rounded-2xl bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-lg">
                <s.icon className="h-8 w-8 text-brand-gold" />
                <h3 className="mt-4 font-display text-lg font-semibold text-brand-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-brand-slate">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-brand-slate/10 bg-white p-8 shadow-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gold/15">
              <Target className="h-6 w-6 text-brand-gold" />
            </div>
            <h2 className="mt-5 font-display text-xl font-bold text-brand-ink">Our mission</h2>
            <p className="mt-3 leading-relaxed text-brand-slate">
              At Alpha Import Export, our mission is to make international commodity sourcing as straightforward as buying
              locally — transparent pricing, verified quality and freight handled end-to-end, so our buyers can focus on
              growing their business instead of chasing suppliers.
            </p>
          </div>
          <div className="rounded-2xl border border-brand-slate/10 bg-white p-8 shadow-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gold/15">
              <Compass className="h-6 w-6 text-brand-gold" />
            </div>
            <h2 className="mt-5 font-display text-xl font-bold text-brand-ink">Our vision</h2>
            <p className="mt-3 leading-relaxed text-brand-slate">
              Our vision is for Alpha Import Export to be the default trade partner for businesses that need dependable
              commodity supply — recognised for reliability, documentation discipline and long-term relationships over
              one-off deals.
            </p>
          </div>
        </div>
      </section>

      <JourneyTimeline />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">Company details</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-brand-slate/10 bg-white shadow-card">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ['Legal name', COMPANY.legalName],
                  ['KBO', COMPANY.kbo],
                  ['EUID', COMPANY.euid],
                  ['VAT', COMPANY.vat],
                  ['Address', COMPANY.address],
                  ['Email', COMPANY.email],
                  ['Phone', COMPANY.phone],
                ].map(([label, value]) => (
                  <tr key={label} className="border-b border-brand-slate/10 last:border-0">
                    <td className="whitespace-nowrap px-5 py-3 font-medium text-brand-ink">{label}</td>
                    <td className="px-5 py-3 text-brand-slate">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 text-center sm:px-6 lg:px-8">
        <button
          onClick={() => onNavigate('catalog')}
          className="rounded-xl bg-brand-navy px-8 py-3 text-sm font-bold text-white transition hover:bg-brand-ocean"
        >
          Browse the catalog
        </button>
      </section>
    </main>
  )
}
